import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopBar from "../../../components/template/TopBar/TopBar";
import { useLocation, useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { InboundItem, MediaItem } from "../../../types/item";
import MediaUploadSection from "./media/index.tsx";
import MediaViewer from "./mediaGallery/mediaViewer";
import ConfirmModal from "../../../components/template/ConfirmModal/ConfirmModal";
import { compressImage } from "../../../lib/image-compressor";
import { generateVideoThumbnail } from "../../../lib/video-thumbnail-generator";
import { putImage, putThumbnail } from "../../../db";
import { v4 as uuidv4 } from "uuid";
import Layout from "../../../components/template/Layout";
import Dropdown, {
  DropdownOption,
} from "../../../components/template/Drowpdown/dropdown";
import AutoResizeTextarea from "../../../components/template/TextArea/AutoResizeTextarea";
import CollapsibleSection from "../../../components/template/CollapsibleSection";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { debounce, isEqual } from "lodash";
import {
  clearFormData,
  updateFormField as updateReduxFormField,
  setFormData as setReduxFormData,
  setPackages,
  setLocations,
  selectPackageOptions,
  selectLocationOptions,
  getFormData,
} from "../../../redux/slices/inboundItemFormSlice";
import {
  updateInboundItem,
  selectHouseLocations,
  getHouseLocations,
  getEvents,
} from "../../../redux/slices/InboundItemsSlice";
import { fetchInspectionTemplates } from "../../../redux/slices/inspectionTemplatesSlice";
import { showToaster } from "../../../redux/slices/toasterSlice";
import { generateYearsOptions } from "../../../utils/common.ts";
import {
  getAccountPackagesandLocations,
  searchAllAccounts,
} from "../../../redux/services/InboundItemsAPI";
import AsyncDropdown from "../../../components/template/Drowpdown/AsyncDropdown";
import { isReallyOnline } from "../../../lib/isOnline.ts";
import { ItemStatus } from "../../../types/enums/item.ts";
import { FaSpinner } from "react-icons/fa";
import { EventStatus } from "../../../types/enums/event";
import { FeatureFlagWrapper } from "../../../components/FeatureFlag/FeatureFlagWrapper";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

const EditItem: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const itemData: InboundItem = location.state?.item;
  const packageOptions = useSelector((state: RootState) =>
    selectPackageOptions(state)
  );
  const locationOptions = useSelector((state: RootState) =>
    selectLocationOptions(state)
  );
  const eventOptions = useSelector((state: RootState) => state.inboundItems.events) || [];
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [mediaUploadProgress, setMediaUploadProgress] = useState<{
    completed: number;
    mediaTotal: number;
  }>({ completed: 0, mediaTotal: 0 });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  // categoryOptions now memoized above
  const [yearsOptions, setYearsOptions] = useState<DropdownOption[]>([]);
  const [isFullscreenView, setIsFullscreenView] = useState(false);
  const [fullscreenMediaIndex, setFullscreenMediaIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaUuidToDelete, setMediaUuidToDelete] = useState<string | null>(
    null
  );
  const [productFamilyOptions, setProductFamilyOptions] = useState<
    DropdownOption[]
  >([]);

  // Local state for immediate UI updates
  const [editItemForm, setEditItemForm] = useState<InboundItem>(
    itemData || ({} as InboundItem)
  );

  const { inspectionTemplates } = useSelector(
    (state: RootState) => state.inspectionTemplates,
    isEqual
  );

  const { token } = useSelector((state: RootState) => state.auth);
  const reduxFormData = useSelector((state: RootState) => getFormData(state));
  const houseLocations = useSelector((state: RootState) =>
    selectHouseLocations(state)
  );
  const enableUnstableFeatures = useFeatureFlag("enableUnstableFeatures");

  // Restore form data from Redux when coming back from media gallery
  useEffect(() => {
    setIsInitializing(true);
    if (reduxFormData && Object.keys(reduxFormData).length > 0) {
      setEditItemForm((prev) => ({ ...prev, ...reduxFormData }));
    } else {
      setEditItemForm(itemData);
      dispatch(setReduxFormData(itemData));
      // Handle category selection (this might trigger more updates)
      handleSelectCategory({
        value: itemData.categoryId?.toString() || "",
        label: itemData.categoryId?.toString() || "",
      });
    }

    // Always set house locations as default
    if (houseLocations && houseLocations.length > 0) {
      dispatch(setLocations(houseLocations));
    }

    if (reduxFormData?.consignorId || itemData?.consignorId) {
      handleGetPackagesAndLocations(
        (reduxFormData?.consignorId || itemData?.consignorId) as number
      );
    }
    fetchLatestData();

    setTimeout(() => {
      setIsInitializing(false);
    }, 100);
    return () => {
      handleFormCleanup();
    };
  }, []);

  // Refresh reference data only (categories, locations, events). Does not clear form.
  const refreshReferenceData = async () => {
    const isOnline = await isReallyOnline();
    if (!isOnline) return;
    try {
      await dispatch(fetchInspectionTemplates({ token: token || "" }));
      await dispatch(getHouseLocations());
      if (enableUnstableFeatures) {
        dispatch(getEvents());
      }
    } catch (error) {
      console.error("Error refreshing reference data:", error);
    }
  };

  const fetchLatestData = async () => {
    await refreshReferenceData();
  };

  // Initialize product family list and inspection state
  useEffect(() => {
    if (itemData.categoryId) {
      generateProductFamilyList(itemData.categoryId);
    }
  }, [itemData?.categoryId]);

  const handleFormCleanup = async () => {
    const nextPath = window.location.pathname;
    const isGoingToListPage =
      nextPath === "/inbound-items" || nextPath === "/inbound-items/";
    const isStayingInEditFlow =
      nextPath.includes("/edit") || nextPath.includes("/media-gallery");

    // Only cleanup if going to list page AND not staying in the edit item flow
    if (isGoingToListPage && !isStayingInEditFlow) {
      dispatch(clearFormData());
    } else {
      console.log("Skipping edit cleanup - staying in edit flow");
    }
  };

  // Keep the redux form updated
  const persistReduxFormField = useCallback(
    debounce((field: keyof InboundItem, value: any) => {
      dispatch(updateReduxFormField({ field, value }));
    }, 500),
    [dispatch]
  );

  // Non-debounced batch update for multiple fields at once
  const persistReduxFormFields = (updates: Partial<InboundItem>) => {
    Object.entries(updates).forEach(([key, value]) => {
      dispatch(
        updateReduxFormField({ field: key as keyof InboundItem, value })
      );
    });
  };

  // Pull-to-refresh: refresh dropdown options only; form state is preserved
  const handleRefresh = async () => {
    await refreshReferenceData();
  };

  // Memoize expensive category processing
  const categoryOptions = useMemo(() => {
    const map = new Map<number, string>();
    inspectionTemplates.forEach((template) => {
      if (template.categoryID && template.categoryDescription) {
        map.set(template.categoryID, template.categoryDescription);
      }
    });

    let categories = Array.from(map.entries()).map(([id, label]) => ({
      value: String(id),
      label,
    }));

    return categories.sort((a, b) => a.label.localeCompare(b.label));
  }, [inspectionTemplates]);

  useEffect(() => {
    setYearsOptions(generateYearsOptions());
  }, []);

  const validateField = useCallback((field: keyof InboundItem, value: any) => {
    switch (field) {
      case "description":
        if (!value) {
          return "Please provide a description";
        }
        break;
      default:
        return "";
    }
    return "";
  }, []);

  const updateFormField = (field: keyof InboundItem, value: any) => {
    setEditItemForm((prev) => ({ ...prev, [field]: value }));
    persistReduxFormField(field, value);
  };

  const updateFormFields = (updates: Partial<InboundItem>) => {
    setEditItemForm((prev) => ({ ...prev, ...updates }));
    persistReduxFormFields(updates); // Use non-debounced batch update
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    // Update local state and Redux
    updateFormField(name as keyof InboundItem, value);

    // Validate the field and update form errors
    const error = validateField(name as keyof InboundItem, value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleTextAreaChange = (value: string, field: string) => {
    // Update local state and Redux
    updateFormField(field as keyof InboundItem, value);

    // Validate the field and update form errors
    const error = validateField(field as keyof InboundItem, value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  // Helper method to process images with batching to prevent browser crashes
  // Processing 40+ images concurrently causes memory exhaustion and "Aw, snap!" crashes
  // Batching prevents this by limiting concurrent Web Workers and IndexedDB operations
  const processImages = async (imageFiles: File[]): Promise<MediaItem[]> => {
    // Process one at a time (BATCH_SIZE = 1) to avoid worker kill on Android when notifications arrive
    const BATCH_SIZE = 1;
    
    console.log(`📦 Processing ${imageFiles.length} images in batches of ${BATCH_SIZE}`);

    const newMediaItems: MediaItem[] = [];

    // Process in batches to avoid overwhelming memory with too many Web Workers
    // Each compression creates a Web Worker, and too many concurrent workers cause crashes
    for (let i = 0; i < imageFiles.length; i += BATCH_SIZE) {
      const batch = imageFiles.slice(i, i + BATCH_SIZE);
      
      // Process batch concurrently
      const batchPromises = batch.map(async (file) => {
        const uuid = uuidv4();
        const compressedFile = await compressImage(file);
        await putImage(uuid, compressedFile);
        
        setMediaUploadProgress(
          (prev: { completed: number; mediaTotal: number }) => ({
            ...prev,
            completed: prev.completed + 1,
          })
        );

        return {
          url: "",
          type: "image",
          type_ID: 1,
          id: uuid,
          uuid: uuid,
          uploaded: false,
        } as MediaItem;
      });

      const batchResults = await Promise.all(batchPromises);
      newMediaItems.push(...batchResults);
      
      // Small delay between batches to allow garbage collection and memory recovery
      if (i + BATCH_SIZE < imageFiles.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    return newMediaItems;
  };

  // Helper method to process videos - always sequential (needs thumbnail generation)
  const processVideos = async (videoFiles: File[]): Promise<MediaItem[]> => {
    const newMediaItems: MediaItem[] = [];
    
    for (const file of videoFiles) {
      const uuid = uuidv4();
      await putImage(uuid, file);

      try {
        const thumbnailBlob = await generateVideoThumbnail(file, 1);
        await putThumbnail(uuid, thumbnailBlob);
      } catch (error) {
        console.warn("Failed to generate thumbnail for video:", error);
        // Continue anyway - we'll show a default video icon
      }

      newMediaItems.push({
        url: "",
        type: "video",
        type_ID: 2,
        id: uuid,
        uuid: uuid,
        uploaded: false,
      });

      setMediaUploadProgress(
        (prev: { completed: number; mediaTotal: number }) => ({
          ...prev,
          completed: prev.completed + 1,
        })
      );
    }

    return newMediaItems;
  };

  const handleMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setMediaUploadProgress({ completed: 0, mediaTotal: files.length });
    setIsUploadingMedia(true);
    try {
      // Filter valid files
      const validFiles: File[] = [];
      for (const file of Array.from(files)) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const isImage =
          ["png", "jpeg", "jpg"].includes(fileExtension || "") &&
          ["image/png", "image/jpeg"].includes(file.type);
        const isVideo =
          ["mp4", "mov"].includes(fileExtension || "") &&
          ["video/mp4", "video/quicktime"].includes(file.type);

        if (isImage || isVideo) {
          validFiles.push(file);
        } else {
          console.error(`Invalid file type: ${file.name}`);
          dispatch(
            showToaster({
              message: `Invalid file type. Only PNG, JPEG, JPG, MP4, and MOV files are supported.`,
              type: "error",
            })
          );
          setIsUploadingMedia(false);
          event.target.value = ""; // Clear input so user can try again
          return;
        }
      }

      if (validFiles.length === 0) {
        setIsUploadingMedia(false);
        event.target.value = ""; // Clear input so user can try again
        return;
      }

      // Create media items with blob storage (not uploaded yet)
      const newMediaItems: MediaItem[] = [];

      // Separate videos and images for different processing
      const videoFiles: File[] = [];
      const imageFiles: File[] = [];

      for (const file of validFiles) {
        if (file.type.startsWith("video/")) {
          videoFiles.push(file);
        } else {
          imageFiles.push(file);
        }
      }

      // Process images using the helper method (concurrent or sequential)
      const imageResults = await processImages(imageFiles);
      newMediaItems.push(...imageResults);

      // Process videos using the helper method (always sequential - needs thumbnail generation)
      const videoResults = await processVideos(videoFiles);
      newMediaItems.push(...videoResults);

      // Update local state and Redux with unified media array
      const updatedMedia = [...(editItemForm?.media || []), ...newMediaItems];
      updateFormField("media", updatedMedia); // Update form state and Redux
    } catch (error) {
      console.error("Error processing media:", error);
    } finally {
      setIsUploadingMedia(false);
      // Clear the input value so the same file can be selected again
      event.target.value = "";
    }
  };

  const removeMedia = async (uuid: string) => {
    // Find the media item being removed by UUID
    const mediaItem = editItemForm.media?.find(
      (media) => media.uuid === uuid || media.id === uuid
    );

    if (!mediaItem) {
      console.warn("Media item not found for deletion:", uuid);
      return;
    }

    const updatedMedia =
      editItemForm.media?.map((media) =>
        media.uuid && media.uuid === uuid || media.id && media.id === uuid
          ? { ...media, toDelete: true, uploaded: true }
          : media
      ) || [];

    updateFormField("media", updatedMedia);

  };

  const requestDeleteMedia = (uuid: string) => {
    setMediaUuidToDelete(uuid);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMedia = async () => {
    console.log("mediaUuidToDelete", mediaUuidToDelete);
    if (mediaUuidToDelete === null) return;

    await removeMedia(mediaUuidToDelete);

    setShowDeleteConfirm(false);
    setMediaUuidToDelete(null);

    // Close fullscreen if no more media
    const media = (editItemForm.media || []).filter((media) => !media.toDelete);
    if (media.length === 0) {
      setIsFullscreenView(false);
    }
  };

  const cancelDeleteMedia = () => {
    setShowDeleteConfirm(false);
    setMediaUuidToDelete(null);
  };

  // Function to check if item data has changed (excluding media)
  const checkHasItemChanges = () => {
    return (
      JSON.stringify({
        ...editItemForm,
        media: undefined,
      }) !==
      JSON.stringify({
        ...itemData,
        media: undefined,
      })
    );
  };

  const hasMediaChanges = () => {
    return (
      JSON.stringify({
        ...editItemForm.media,
      }) !==
      JSON.stringify({
        ...itemData.media,
      })
    );
  };

  const canSave = () => {
    return (
      (!isUploadingMedia &&
        !editItemDisabled &&
        mediaUploadProgress.completed >= mediaUploadProgress.mediaTotal &&
        checkHasItemChanges()) ||
      hasMediaChanges()
    );
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);

      // Check if item data has changed (excluding media)
      const hasItemChanges = checkHasItemChanges();

      const updatedItemData = {
        ...editItemForm,
        isInbound: true,
        tempId: editItemForm.tempId || generateTempId(), // Use existing tempId if available
        status_ID: ItemStatus.Pending,
      };

      // Dispatch the update thunk - it handles IndexedDB save, API sync, and media deletion
      await dispatch(
        updateInboundItem({ updatedItemData, hasItemChanges })
      ).unwrap();

      // Small delay to ensure user sees the saving state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Still navigate even if API fails (item is saved locally)
      navigate("/inbound-items");
    } catch (error) {
      console.error("Error updating item:", error);
      navigate("/inbound-items");
    } finally {
      setIsSaving(false);
    }
  };

  const generateTempId = () => {
    // Generate a random negative ID using crypto
    const randomBytes = new Uint32Array(1);
    crypto.getRandomValues(randomBytes);
    return randomBytes[0];
  };

  const handleSelectProductFamily = (option: DropdownOption) => {
    updateFormField("familyId" as keyof InboundItem, parseInt(option.value));
  };

  const handleDisableInspection = () => {
    updateFormField("categoryId", 0);
    updateFormField("familyId", 0);
    updateFormField("inspection", undefined);
  };

  const handleSelectCategory = (option: DropdownOption) => {
    const categoryId = parseInt(option.value);
    updateFormField("categoryId", categoryId);

    if (option.value !== "0") {
      const productFamilyList = generateProductFamilyList(
        parseInt(option.value)
      );
      setProductFamilyOptions(productFamilyList);
    } else {
      handleDisableInspection();
    }
  };

  const generateProductFamilyList = useCallback(
    (categoryId: number) => {
      let families = inspectionTemplates.filter(
        (i) =>
          i.tenant_ID === 101 &&
          i.description !== null &&
          i.description.length > 0 &&
          i.categoryID === Number(categoryId)
      );

      let list: { value: string; label: string }[] = [];

      families.forEach((i) => {
        list.push({
          value: String(i.id),
          label: i.description,
        });
      });
      return list;
    },
    [inspectionTemplates]
  );

  const handleSearchConsignors = async (searchTerm: string) => {
    if (await isReallyOnline()) {
      const response = await searchAllAccounts(token || "", {
        searchString: searchTerm,
      });
      return response.data.map((item: any) => ({
        value: item.id.toString(),
        label: item.accountName,
        consignorCode: item.consignorCode,
      }));
    }
    return [];
  };

  const handleSelectConsignor = (option: any) => {
    updateFormFields({
      consignorId: parseInt(option.value),
      consignor: option.label,
      consignorCode: option.consignorCode,
      packageId: 0,
      package: "",
      locationId: 0,
      location: "",
    });

    handleGetPackagesAndLocations(parseInt(option.value));
  };

  const handleClearConsignor = () => {
    updateFormFields({
      consignorId: 0,
      consignor: "",
      packageId: 0,
      package: "",
      locationId: 0,
      location: "",
    });

    // Clear Redux state (raw data)
    dispatch(setPackages([]));
    dispatch(setLocations(houseLocations));

    // Local state will be cleared via useEffect watching Redux
  };

  const handleGetPackagesAndLocations = async (account_ID: number) => {
    const isOnline = await isReallyOnline();
    if (!isOnline) return;
    try {
      const response = await getAccountPackagesandLocations(token || "", {
        accountId: account_ID,
      });

      // Save raw API response to Redux
      dispatch(setPackages(response.data.packages));

      // Use consignor locations if available, otherwise fall back to house locations
      const locations =
        response.data.locations && response.data.locations.length > 0
          ? response.data.locations
          : houseLocations;
      dispatch(setLocations(locations));

      // Transformed options will be available via selectors
      // and automatically update local state through useEffect
    } catch (error) {
      console.error("Error getting packages and locations:", error);
      // On error, still use house locations
      if (houseLocations && houseLocations.length > 0) {
        dispatch(setLocations(houseLocations));
      }
    }
  };

  // Show loading state while fetching data or initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <TopBar title="Edit Item" showBackIcon={true} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleViewMedia = (selectedMedia: MediaItem) => {
    const media = (editItemForm.media || []).filter((media) => !media.toDelete);
    const index = media.findIndex((m) => m.id === selectedMedia.id);
    setFullscreenMediaIndex(index >= 0 ? index : 0);
    setIsFullscreenView(true);
  };

  const handleViewAllMedia = () => {
    const params = {
      media: (editItemForm.media || []).filter((media) => !media.toDelete),
      itemTitle: editItemForm.description || "Media Gallery",
    };
    navigate(`/inbound-items/create-item/media-gallery`, {
      state: params,
    });
  };

  const closeFullscreenView = () => {
    setIsFullscreenView(false);
  };

  const goToGallery = () => {
    setIsFullscreenView(false);
    handleViewAllMedia();
  };

  const nextMedia = () => {
    const media = (editItemForm.media || []).filter((media) => !media.toDelete);
    if (media.length > 1) {
      setFullscreenMediaIndex((prev) => (prev + 1) % media.length);
    }
  };

  const prevMedia = () => {
    const media = (editItemForm.media || []).filter((media) => !media.toDelete);
    if (media.length > 1) {
      setFullscreenMediaIndex(
        (prev) => (prev - 1 + media.length) % media.length
      );
    }
  };

  // Delete functions removed - functionality moved to media gallery

  // const editItemDisabled = editItemForm.status_ID === ItemStatus.Pending;
  const editItemDisabled = false;

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      refreshingContent={
        <div className="flex justify-center items-center mt-4">
          <div className="w-8 h-8 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
        </div>
      }
    >
      <>
        <div className="bg-gray-100 min-h-screen">
          <Layout title="Edit Item" showBackIcon={true}>
            <div className="p-4 pb-[140px]">
              <div>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-md text-sm"
                  placeholder="Title/Lead Description"
                  name="description"
                  disabled={editItemDisabled}
                  value={editItemForm.description || ""}
                  onChange={handleInputChange}
                />
                {formErrors.description && (
                  <p className="text-red-500 text-sm mt-1 ml-1">
                    {formErrors.description}
                  </p>
                )}
              </div>
              <div className="mt-4">
                <AutoResizeTextarea
                  disabled={editItemDisabled}
                  value={editItemForm.details || ""}
                  onChange={(val: any) => handleTextAreaChange(val, "details")}
                  placeholder="Details"
                />
                {formErrors.details && (
                  <p className="text-red-500 text-sm mt-1 ml-1">
                    {formErrors.details}
                  </p>
                )}
              </div>

              {/* Media Section */}
              <MediaUploadSection
                media={(editItemForm.media || []).filter(
                  (media) => !media.toDelete
                )}
                onMediaUpload={handleMediaUpload}
                mediaUploadProgress={mediaUploadProgress}
                onMediaRemove={(e, uuid) => {
                  console.log("onMediaRemove", uuid);
                  e.stopPropagation();
                  requestDeleteMedia(uuid);
                }}
                onViewMedia={handleViewMedia}
                onViewAllMedia={handleViewAllMedia}
                isUploadingMedia={isUploadingMedia}
                allowUpload={!editItemDisabled}
              />

              {/* Consignor Information Section */}
              <CollapsibleSection
                title="Consignor Information"
                defaultExpanded={false}
              >
                <div className="">
                  <AsyncDropdown
                    onSearch={handleSearchConsignors}
                    emptyMessage="No consignors found"
                    loadingMessage="Loading consignors..."
                    placeholder="Select Consignor"
                    onSelect={(option) => handleSelectConsignor(option)}
                    onClear={handleClearConsignor}
                    value={editItemForm?.consignor || ""}
                    minSearchLength={2}
                    searchDebounceMs={1000}
                    allowClear={true}
                  />
                  {formErrors.consignorId && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.consignorId}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <Dropdown
                    options={packageOptions}
                    isSearchable={true}
                    placeholder="Select Package"
                    allowClear={true}
                    onClear={() => {
                      updateFormFields({
                        packageId: 0,
                        package: "",
                      });
                    }}
                    value={editItemForm?.package || ""}
                    disabled={
                      !packageOptions ||
                      packageOptions.length === 0 ||
                      editItemDisabled
                    }
                    onSelect={(option) => {
                      updateFormField("packageId", parseInt(option.value));
                      updateFormField("package", option.label);
                    }}
                  />
                  {formErrors.packageId && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.packageId}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="Consignor Code"
                    name="consignorCode"
                    value={editItemForm?.consignorCode || ""}
                    onChange={handleInputChange}
                    disabled={
                      editItemDisabled ||
                      Boolean(
                        editItemForm?.consignorId &&
                          editItemForm?.consignorId !== 0
                      )
                    }
                  />
                  {formErrors.consignorCode && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.consignorCode}
                    </p>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Location" defaultExpanded={false}>
                <div className="mt-4">
                  <Dropdown
                    options={locationOptions}
                    isSearchable={true}
                    placeholder="Select Location"
                    allowClear={true}
                    onClear={() => {
                      updateFormFields({
                        locationId: 0,
                        location: "",
                      });
                    }}
                    value={
                      editItemForm?.locationId
                        ? (
                            locationOptions.find(
                              (option) =>
                                option.value ===
                                editItemForm.locationId?.toString()
                            ) as DropdownOption
                          )?.label || ""
                        : ""
                    }
                    disabled={
                      !locationOptions ||
                      locationOptions.length === 0 ||
                      editItemDisabled
                    }
                    onSelect={(option) => {
                      updateFormField("locationId", parseInt(option.value));
                      updateFormField("location", option.label);
                    }}
                  />
                  {formErrors.locationId && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.locationId}
                    </p>
                  )}
                </div>
              </CollapsibleSection>

              {/* Optional Attributes Section */}
              <CollapsibleSection
                title="Item Data Points"
                defaultExpanded={false}
              >
                <div>
                  <Dropdown
                    options={[
                      { value: "0", label: "Select Category" },
                      ...categoryOptions,
                    ]}
                    isSearchable={true}
                    placeholder="Category"
                    onSelect={(option) => handleSelectCategory(option)}
                    disabled={editItemDisabled}
                    value={
                      editItemForm.categoryId
                        ? editItemForm.categoryId?.toString()
                        : "0"
                    }
                  />
                </div>
                {productFamilyOptions.length > 0 && (
                  <div className="mt-4">
                    <Dropdown
                      options={[
                        { value: "0", label: "Select Product Family" },
                        ...productFamilyOptions,
                      ]}
                      isSearchable={true}
                      placeholder="Select Product Family"
                      onSelect={(option) => handleSelectProductFamily(option)}
                      value={editItemForm.familyId?.toString() || ""}
                      disabled={editItemDisabled}
                    />
                  </div>
                )}
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="Asset Number"
                    name="assetNumber"
                    disabled={editItemDisabled}
                    value={editItemForm.assetNumber || ""}
                    onChange={handleInputChange}
                  />
                  {formErrors.assetNumber && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.assetNumber}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="Stock Number"
                    name="stockNumber"
                    disabled={editItemDisabled}
                    value={editItemForm.stockNumber || ""}
                    onChange={handleInputChange}
                  />
                  {formErrors.stockNumber && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.stockNumber}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="VIN"
                    name="vinSn"
                    disabled={editItemDisabled}
                    value={editItemForm.vinSn || ""}
                    onChange={handleInputChange}
                  />
                  {formErrors.vinSn && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.vinSn}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <Dropdown
                    options={yearsOptions}
                    isSearchable={true}
                    placeholder="Year"
                    disabled={editItemDisabled}
                    value={editItemForm?.year?.toString() || ""}
                    onSelect={(option) =>
                      handleInputChange({
                        target: { name: "year", value: option.value },
                      })
                    }
                  />
                  {formErrors.year && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.year}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    placeholder="Make"
                    name="make"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    disabled={editItemDisabled}
                    value={editItemForm?.make?.toString() || ""}
                    onChange={handleInputChange}
                  />
                  {formErrors.make && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.make}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm"
                    placeholder="Model"
                    name="model"
                    disabled={editItemDisabled}
                    value={editItemForm?.model?.toString() || ""}
                    onChange={handleInputChange}
                  />
                  {formErrors.model && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.model}
                    </p>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Notes" defaultExpanded={false}>
                <div>
                  <AutoResizeTextarea
                    disabled={editItemDisabled}
                    value={editItemForm.notes || ""}
                    onChange={(val: any) => handleTextAreaChange(val, "notes")}
                    placeholder="Add any additional notes..."
                    name="notes"
                    className="mb-0 pb-0"
                  />
                  <p className="text-gray-500 text-xs">
                    For Internal Use Only
                  </p>

                  {formErrors.notes && (
                    <p className="text-red-500 text-sm mt-1 ml-1">
                      {formErrors.notes}
                    </p>
                  )}
                </div>
              </CollapsibleSection>

              <FeatureFlagWrapper flag="enableUnstableFeatures">
                <CollapsibleSection title="Event Information" defaultExpanded={false}>
                  <div className="mt-4">
                    <Dropdown
                      options={eventOptions.map((option) => ({
                        value: option.value,
                        label: option.label + (option.eventStatus_ID === EventStatus.In_Progress ? " (In Progress)" : option.isRunOrderLocked ? " (Run Order Locked)" : ""),
                        disabled: option.eventStatus_ID === EventStatus.In_Progress || option.isRunOrderLocked,
                      }))}
                      isSearchable={true}
                      allowClear={true}
                      onClear={() => {
                        updateFormFields({
                          eventId: undefined,
                          lotNumber: "",
                        });
                      }}
                      placeholder="Select Event"
                      value={editItemForm?.eventId?.toString() || ""}
                      disabled={editItemDisabled}
                      onSelect={(option) =>
                        handleInputChange({
                          target: { name: "eventId", value: option.value },
                        })
                      }
                    />
                    {formErrors.eventId && (
                      <p className="text-red-500 text-sm mt-1 ml-1">
                        {formErrors.eventId}
                      </p>
                    )}
                  </div>
                  <div className="mt-4">
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-md text-sm"
                      placeholder="Lot Number"
                      name="lotNumber"
                      value={editItemForm?.lotNumber?.toString() || ""}
                      onChange={handleInputChange}
                      disabled={Boolean(!editItemForm?.eventId) || editItemDisabled}
                    />
                    {formErrors.lotNumber && (
                      <p className="text-red-500 text-sm mt-1 ml-1">
                        {formErrors.lotNumber}
                      </p>
                    )}
                  </div>
                </CollapsibleSection>
              </FeatureFlagWrapper>
            </div>
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white rounded-t-2xl">
              <button
                className={
                  "w-full bg-primary text-white rounded-md p-3 shadow-button-shadow " +
                  (canSave() ? " opacity-100" : " opacity-20")
                }
                // className="w-full bg-primary text-white rounded-md p-3 shadow-button-shadow opacity-90 hover:opacity-100"
                onClick={handleSaveChanges}
                disabled={!canSave()}
              >
                {isSaving ? (
                  <div className="py-1 flex items-center justify-center">
                    <FaSpinner className="animate-spin mr-1" />
                    Saving...
                  </div>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </Layout>
        </div>

        {/* Fullscreen Media Overlay */}
        {isFullscreenView &&
          (editItemForm.media || []).filter((media) => !media.toDelete).length >
            0 && (
            <MediaViewer
              media={(editItemForm.media || []).filter(
                (media) => !media.toDelete
              )}
              currentIndex={fullscreenMediaIndex}
              onClose={closeFullscreenView}
              onNext={nextMedia}
              onPrev={prevMedia}
              onViewGallery={goToGallery}
              onDelete={() => {
                console.log("onDelete");
                const media = (editItemForm.media || []).filter(
                  (media) => !media.toDelete
                );
                const currentMedia = media[fullscreenMediaIndex];
                if (currentMedia) {
                  console.log("currentMedia", currentMedia);
                  const uuid = currentMedia.uuid || currentMedia.id || "";
                  if (uuid) {
                    requestDeleteMedia(uuid);
                  }
                }
              }}
            />
          )}

        <ConfirmModal
          isOpen={showDeleteConfirm}
          title="Delete Media"
          message="Are you sure you want to delete this media? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteMedia}
          onCancel={cancelDeleteMedia}
        />
      </>
    </PullToRefresh>
  );
};

export default EditItem;
